<!-- 
 自定义消息弹框的UI组件，负责渲染和动画效果，与 useCustomNotify.ts 配合使用
-->
<template>
    <Teleport to="body">
        <Transition name="slide-notify" @after-enter="handleAfterEnter" @before-leave="handleBeforeLeave">
            <div v-if="notifyState.visible" class="custom-notify-overlay">
                <div class="custom-notify" @click="handleClick">
                    <div class="notify-content">
                        <span class="notify-message">{{ notifyState.message }}</span>
                    </div>
                    <div class="notify-progress">
                        <svg class="stroke-swiper" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                            <circle class="stroke-circle" cx="50" cy="50" r="43" fill="none" stroke-width="16"></circle>
                        </svg>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup lang="ts">
import { watch, onUnmounted } from 'vue'
import { notifyState, hideCustomNotify } from './useCustomNotify'

let timer: number | null = null

const handleClick = () => {
    if (notifyState.value.onClick) {
        notifyState.value.onClick()
    }
}

const startCountdown = () => {
    // 使用传入的 duration 作为总显示时间
    const totalDuration = notifyState.value.duration

    // 动态设置CSS变量用于进度条动画时长
    const durationInSeconds = totalDuration / 1000
    document.documentElement.style.setProperty('--progress-duration', `${durationInSeconds}s`)

    timer = setTimeout(() => {
        hideCustomNotify()
    }, totalDuration)
}

const stopCountdown = () => {
    if (timer) {
        clearTimeout(timer)
        timer = null
    }
    // 重置CSS变量
    document.documentElement.style.removeProperty('--progress-duration')
}

const handleAfterEnter = () => {
    // 动画完成后立即启动倒计时
    startCountdown()
}
const handleBeforeLeave = () => {
    // 离开动画开始时，停止倒计时
    stopCountdown()
}

// 监听visible变化
watch(
    () => notifyState.value.visible,
    (newVal) => {
        if (!newVal) {
            // 当通知隐藏时，停止倒计时
            stopCountdown()
        }
    },
    { immediate: true } // 添加 immediate，以在组件首次加载时就监听
)

onUnmounted(() => {
    stopCountdown()
    // 确保清理CSS变量
    document.documentElement.style.removeProperty('--progress-duration')
})
</script>

<style scoped lang="scss">
.custom-notify-overlay {
    position: fixed;
    top: calc(var(--home-header) + 10px);
    left: 28px;
    right: 28px;
    pointer-events: none;
    z-index: 10000;

    .custom-notify {
        position: absolute;
        left: 0;
        right: 0;
        min-height: 70px;
        border-radius: 20px;
        display: flex;
        align-items: center;
        padding: 16px 12px 16px 20px;
        pointer-events: auto;

        background: hsla(0, 0%, 7%, 0.7);
        -webkit-backdrop-filter: blur(139.561px);
        backdrop-filter: blur(139.561px);
        box-shadow: 0 0 6.978px rgba(0, 0, 0, 0.1);

        .notify-content {
            flex: 1;
            display: flex;
            align-items: center;
            min-width: 0; // 防止文本溢出

            .notify-message {
                color: #fff;
                font-size: 22px;
                line-height: 1.4;
                word-break: break-word;
                overflow-wrap: break-word;
                hyphens: auto;
            }
        }

        .notify-progress {
            margin-left: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0; // 防止进度条被压缩
            position: relative;
            height: 30.7034px;
            width: 30.7034px;

            .stroke-swiper {
                height: 30.7034px;
                width: 30.7034px;
                position: absolute;
                transform: rotate(-90deg);
            }

            .stroke-circle {
                stroke: #24ee89;
                stroke-dasharray: 283;
                stroke-dashoffset: 283;
                stroke-linecap: round;
                animation: stroke-draw var(--progress-duration, 2s) ease-in-out forwards;
            }
        }
    }
}

// 滑入滑出动画
.slide-notify-enter-active,
.slide-notify-leave-active {
    transition: transform 0.3s, opacity 0.3s;
}

.slide-notify-enter-from,
.slide-notify-leave-to {
    transform: translateX(100%);
}

// 进度条动画
@keyframes stroke-draw {
    0% {
        stroke-dashoffset: 283;
    }
    to {
        stroke-dashoffset: 0;
    }
}
</style>
